import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCompanyIntegrations, getCompanyIntegrationsHealth } from '../api';
import { getIntegrations } from '../../Authentication/api';
import IntegrationsGrid from '../components/Integrations/IntegrationsGrid';
import { DashboardPage, DashboardHeader } from '../components/PremiumDesign';
import { Button, SkeletonCard } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';

export function IntegrationsPage() {
  const navigate = useNavigate();

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  // Query: Fetch company integrations status
  const {
    data: companyIntegrationsRaw,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-integrations', companyId],
    queryFn: () => getCompanyIntegrations(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  // Query: Fetch company integrations live health status list
  const { data: integrationsHealthRaw = [] } = useQuery({
    queryKey: ['company-integrations-health', companyId],
    queryFn: () => getCompanyIntegrationsHealth(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const companyIntegrations = companyIntegrationsRaw || [];

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && companyIntegrationsRaw !== undefined,
    300,
    500,
  );

  // Build health map keyed by integration slug / ID
  const getIntegrationsHealthMap = () => {
    const map = {};
    if (Array.isArray(integrationsHealthRaw)) {
      integrationsHealthRaw.forEach((item) => {
        const slug = item.integration_slug;
        if (slug) {
          map[slug] = item;
          if (slug === 'whatsapp') {
            map['whatsapp-business'] = item;
          }
        }
      });
    }
    return map;
  };

  const integrationsHealthMap = getIntegrationsHealthMap();

  const { data: catalogRaw, isLoading: catalogLoading } = useQuery({
    queryKey: ['integrations-catalog'],
    queryFn: getIntegrations,
    staleTime: 60_000,
  });

  const catalogItems = Array.isArray(catalogRaw)
    ? catalogRaw
    : Array.isArray(catalogRaw?.results)
      ? catalogRaw.results
      : [];
  const showSkeleton =
    isInitialLoading || catalogLoading || showDelayedSkeleton;
  const windexCatalog = catalogItems.find((item) => item?.slug === 'windex');
  const windexCompany = companyIntegrations.find(
    (item) => item.integration_slug === 'windex',
  );
  const extraApps = windexCatalog
    ? [
        {
          id: 'windex',
          name:
            windexCatalog.name || windexCompany?.integration_name || 'Windex',
          category: 'Integrations',
          logoType: 'windex',
          installed: !!windexCompany?.is_connected,
          description:
            windexCatalog.description || 'Connect a school with a school code.',
        },
      ]
    : [];

  // Derive installed state from API status and health list
  const getInstalledAppsMap = () => {
    const baseInstalled = {
      'whatsapp-business': false,
      instagram: false,
      facebook: false,
      threads: false,
      google_workspace: false,
      other_email: false,
    };

    if (companyIntegrations && Array.isArray(companyIntegrations)) {
      companyIntegrations.forEach((item) => {
        if (item.is_connected) {
          const slug = item.integration_slug;
          if (slug === 'whatsapp') {
            baseInstalled['whatsapp-business'] = true;
          } else {
            baseInstalled[slug] = true;
          }
        }
      });
    }

    if (Array.isArray(integrationsHealthRaw)) {
      integrationsHealthRaw.forEach((item) => {
        const slug = item.integration_slug;
        const key = slug === 'whatsapp' ? 'whatsapp-business' : slug;
        if (
          item.lifecycle_status === 'activated' ||
          (item.health_status && item.health_status !== 'disconnected')
        ) {
          baseInstalled[key] = true;
        }
      });
    }

    return baseInstalled;
  };

  const installedApps = getInstalledAppsMap();

  const getIntegrationNamesMap = () => {
    const names = {};
    if (companyIntegrations && Array.isArray(companyIntegrations)) {
      companyIntegrations.forEach((item) => {
        const slug = item.integration_slug;
        const key = slug === 'whatsapp' ? 'whatsapp-business' : slug;
        if (item.integration_name) {
          names[key] = item.integration_name.replace(
            /Other Email/gi,
            'Business Email',
          );
        }
      });
    }
    if (Array.isArray(integrationsHealthRaw)) {
      integrationsHealthRaw.forEach((item) => {
        const slug = item.integration_slug;
        const key = slug === 'whatsapp' ? 'whatsapp-business' : slug;
        if (item.integration_name && !names[key]) {
          names[key] = item.integration_name.replace(
            /Other Email/gi,
            'Business Email',
          );
        }
      });
    }
    return names;
  };

  const integrationNames = getIntegrationNamesMap();

  const handleSelectApp = (app) => {
    if (app.id === 'instagram') {
      navigate('/integrations/instagram-business');
    } else if (app.id === 'google_workspace') {
      navigate('/integrations/google-workspace');
    } else if (app.id === 'other_email') {
      navigate('/integrations/other-email');
    } else {
      navigate(`/integrations/${app.id}`);
    }
  };

  const handleConnectInstagram = () => {
    navigate('/integrations/instagram-business');
  };

  if (showSkeleton || !companyIntegrationsRaw) {
    return (
      <DashboardPage>
        <DashboardHeader title="Integrations Panel" />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-6">
          {[...Array(10)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </DashboardPage>
    );
  }

  if (isError) {
    return (
      <DashboardPage>
        <DashboardHeader title="Integrations Panel" />
        <div className="w-full flex flex-col items-center justify-center min-h-[300px] gap-4 font-sf-pro-display text-center mt-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 shadow-lg shadow-red-500/5">
            <svg
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-text-primary">
              Failed to Load Integrations
            </h3>
            <p className="text-xs text-text-secondary mt-1 max-w-xs mx-auto">
              {error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'There was an error fetching your integrations. Please try again.'}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => refetch()}
            className="mt-2 py-2.5 px-6 text-xs font-bold rounded-button shadow-lg h-auto"
          >
            Retry Loading
          </Button>
        </div>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage>
      <div className="w-full mt-2">
        <IntegrationsGrid
          onSelectApp={handleSelectApp}
          onConnectInstagram={handleConnectInstagram}
          installedApps={installedApps}
          integrationNames={integrationNames}
          healthMap={integrationsHealthMap}
          companyIntegrations={companyIntegrations}
          extraApps={extraApps}
          catalogSlugs={catalogItems.map((item) => item?.slug).filter(Boolean)}
          companyId={companyId}
        />
      </div>
    </DashboardPage>
  );
}
